import { MEN_ALONE_SPA_SLUGS } from "../bookings/guest-mix";

/**
 * Venue sub-type tags — the controlled vocabulary behind the Discover
 * Restaurants sub-filter chips. Stored on `activities.tags` as canonical
 * keys (not localized); the UI renders `labelKey` via i18n.
 * Single source of truth for web, mobile, admin, and API validation.
 */
export interface RestaurantSubtag {
  readonly id: string;
  /** i18n key present in every app's locale files. */
  readonly labelKey: string;
}

export const RESTAURANT_SUBTAGS: readonly RestaurantSubtag[] = [
  { id: "rooftop", labelKey: "discover.subtag.rooftop" },
  { id: "bar", labelKey: "discover.subtag.bar" },
  { id: "live-music", labelKey: "discover.subtag.live-music" },
  { id: "lounge", labelKey: "discover.subtag.lounge" },
  { id: "cafe", labelKey: "discover.subtag.cafe" },
] as const;

export const RESTAURANT_SUBTAG_IDS: readonly string[] =
  RESTAURANT_SUBTAGS.map((t) => t.id);

export type RestaurantTag = (typeof RESTAURANT_SUBTAGS)[number]["id"];

/**
 * Collection tags: the chip row on the restaurants hub (owner, 2026-09-30).
 * Stored in the same `activities.tags` column as the sub-tags, but kept out
 * of RESTAURANT_SUBTAGS so the Discover sheets on web and mobile do not grow
 * three chips that only bookable venues carry. `rooftop` and `live-music`
 * are shared with the sub-tags, so the explore venues already tagged with
 * them show up under those chips too.
 */
export interface RestaurantCollection {
  readonly id: string;
  /** Remix icon name for the chip. */
  readonly icon: string;
}

export const RESTAURANT_COLLECTIONS: readonly RestaurantCollection[] = [
  { id: "dinner-show", icon: "ri-sparkling-2-line" },
  { id: "live-music", icon: "ri-music-2-line" },
  { id: "rooftop", icon: "ri-building-2-line" },
  { id: "terrace", icon: "ri-plant-line" },
  { id: "late-night", icon: "ri-moon-clear-line" },
] as const;

/**
 * The chip row on the spas hub (owner, 2026-10-25): Hammam, Couples, Men
 * welcome, Under 300 MAD. Hammam and Couples are tags set per spa in the
 * admin, like the restaurant collections. Men welcome and Under 300 are
 * never stored: the API works them out on every list (`spaDerivedTags`), so
 * they cannot drift from the booking rule or the price.
 */
export const SPA_COLLECTIONS: readonly RestaurantCollection[] = [
  { id: "hammam", icon: "ri-drop-line" },
  { id: "couples", icon: "ri-user-heart-line" },
  { id: "men-welcome", icon: "ri-user-line" },
  { id: "under-300", icon: "ri-price-tag-3-line" },
] as const;

/** The "Under 300 MAD" chip's ceiling, in dirhams, on the lead price per person. */
export const SPA_BUDGET_MAD = 300;

/** The spa chips the API derives; never stored on a row, never offered in the admin. */
export const SPA_DERIVED_TAG_IDS: readonly string[] = ["men-welcome", "under-300"];

/** The spa chips the admin sets per venue. */
export const SPA_TAG_IDS: readonly string[] = SPA_COLLECTIONS.map((c) => c.id).filter(
  (id) => !SPA_DERIVED_TAG_IDS.includes(id),
);

/**
 * The derived spa chips one spa carries: "men-welcome" when it takes a man on
 * his own (MEN_ALONE_SPA_SLUGS, the same list the booking checks), and
 * "under-300" when its lead price per person is at most SPA_BUDGET_MAD.
 * Judged in dirhams, before any currency conversion, so the chip means the
 * same spas whatever currency the guest reads prices in.
 */
export function spaDerivedTags(slug: string, priceMad: number): string[] {
  const out: string[] = [];
  if (MEN_ALONE_SPA_SLUGS.has(slug)) out.push("men-welcome");
  if (priceMad > 0 && priceMad <= SPA_BUDGET_MAD) out.push("under-300");
  return out;
}

/** The chip row of a hub page, or none. */
export function hubCollections(category: string | null | undefined): readonly RestaurantCollection[] {
  if (category === "restaurants") return RESTAURANT_COLLECTIONS;
  if (category === "spas") return SPA_COLLECTIONS;
  return [];
}

/**
 * Cuisine chips: a second row on the restaurants hub, above the collections
 * (owner, 2026-10-30, mockup 3). Stored in `activities.tags` like the
 * collections, one or two per restaurant, set from the venue's own menu or
 * listing (Japanese counts as Asian). A separate filter from `tag` (the
 * API's `cuisine`), so a guest can ask for Moroccan and Rooftops at once.
 * Ordered by how many venues carry each, which is the order of the row.
 */
export const RESTAURANT_CUISINE_IDS: readonly string[] = [
  "moroccan",
  "mediterranean",
  "lebanese",
  "asian",
  "french",
  "italian",
  "latin",
  "american",
] as const;

/** True when `id` is one of the cuisine chips; anything else in a URL is dropped. */
export function isRestaurantCuisine(id: string | null | undefined): id is string {
  return !!id && RESTAURANT_CUISINE_IDS.includes(id);
}

/**
 * The cuisine a restaurant card names, e.g. "Moroccan" for "Moroccan ·
 * Marrakech" (owner, 2026-10-31). The first cuisine in the row's tags: the
 * fill put a venue's main cuisine first. Null when it has none.
 */
export function primaryCuisine(tags: readonly string[] | null | undefined): string | null {
  return tags?.find((t) => isRestaurantCuisine(t)) ?? null;
}

/** Every key `activities.tags` may hold: the sub-tags, collections and cuisines. */
export const ACTIVITY_TAG_IDS: readonly string[] = [
  ...new Set([
    ...RESTAURANT_SUBTAG_IDS,
    ...RESTAURANT_COLLECTIONS.map((c) => c.id),
    ...SPA_TAG_IDS,
    ...RESTAURANT_CUISINE_IDS,
  ]),
];
